import React from 'react';
import {
    X, Clock, Send, Download,
    Building2, Mail, Phone, FileText, CheckCircle, AlertCircle,
    CreditCard, Printer
} from 'lucide-react';
import { Invoice } from '../../services/billing.service';
import { useBillingStore } from '../../store/billingStore';

interface ViewInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    invoice: Invoice | null;
    onRecordPayment: () => void;
    onSend: () => void;
}

const ViewInvoiceModal: React.FC<ViewInvoiceModalProps> = ({
    isOpen,
    onClose,
    invoice,
    onRecordPayment,
    onSend
}) => {
    const { sendReminder } = useBillingStore();

    if (!isOpen || !invoice) return null;

    const formatCurrency = (amount: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(amount);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft':
                return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
            case 'sent':
            case 'viewed':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'partial':
                return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'paid':
                return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'overdue':
                return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            case 'cancelled':
                return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'paid':
                return <CheckCircle className="w-4 h-4" />;
            case 'overdue':
                return <AlertCircle className="w-4 h-4" />;
            case 'sent':
            case 'viewed':
                return <Send className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const getDaysUntilDue = () => {
        const due = new Date(invoice.dueDate);
        const today = new Date();
        const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const daysUntilDue = getDaysUntilDue();
    const isPaid = invoice.status === 'paid';
    const canRecordPayment = ['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status);

    const handleSendReminder = async () => {
        try {
            await sendReminder(invoice.id);
        } catch (error) {
            console.error('Failed to send reminder:', error);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                {/* Backdrop */}
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

                {/* Modal */}
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                                <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {invoice.invoiceNumber}
                                </h2>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                                        {getStatusIcon(invoice.status)}
                                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {invoice.type}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
                        {/* Amount & Due Date Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-100 dark:border-green-800">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
                                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-1">
                                    {formatCurrency(invoice.total, invoice.currency)}
                                </p>
                                {invoice.amountPaid > 0 && (
                                    <div className="flex items-center gap-2 mt-2 text-sm">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        <span className="text-gray-600 dark:text-gray-400">
                                            Paid: {formatCurrency(invoice.amountPaid, invoice.currency)}
                                        </span>
                                    </div>
                                )}
                                {invoice.amountDue > 0 && !isPaid && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        Due: {formatCurrency(invoice.amountDue, invoice.currency)}
                                    </p>
                                )}
                            </div>

                            <div className={`p-4 rounded-xl border ${daysUntilDue < 0 && !isPaid
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800'
                                : daysUntilDue <= 7 && !isPaid
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-100 dark:border-yellow-800'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-100 dark:border-gray-700'
                                }`}>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Due Date</p>
                                <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                                    {formatDate(invoice.dueDate)}
                                </p>
                                {!isPaid && (
                                    <p className={`text-sm mt-2 ${daysUntilDue < 0
                                        ? 'text-red-600 dark:text-red-400 font-medium'
                                        : daysUntilDue <= 7
                                            ? 'text-yellow-600 dark:text-yellow-400'
                                            : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                        {daysUntilDue < 0
                                            ? `${Math.abs(daysUntilDue)} days overdue`
                                            : daysUntilDue === 0
                                                ? 'Due today'
                                                : `${daysUntilDue} days left`}
                                    </p>
                                )}
                                {isPaid && invoice.paidAt && (
                                    <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                                        Paid on {formatDate(invoice.paidAt)}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Bill To / Bill From */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Bill To</h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium text-gray-900 dark:text-white">{invoice.billTo.companyName}</span>
                                    </div>
                                    {invoice.billTo.contactName && (
                                        <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">{invoice.billTo.contactName}</p>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{invoice.billTo.email}</span>
                                    </div>
                                    {invoice.billTo.phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">{invoice.billTo.phone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Bill From</h4>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium text-gray-900 dark:text-white">{invoice.billFrom.businessName}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{invoice.billFrom.email}</span>
                                    </div>
                                    {invoice.billFrom.phone && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm text-gray-600 dark:text-gray-400">{invoice.billFrom.phone}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Line Items</h4>
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-gray-600">
                                            <th className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 py-3 px-4">Description</th>
                                            <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 py-3 px-4 w-20">Qty</th>
                                            <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 py-3 px-4 w-28">Unit Price</th>
                                            <th className="text-right text-xs font-medium text-gray-500 dark:text-gray-400 py-3 px-4 w-28">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoice.lineItems.map((item, idx) => (
                                            <tr key={idx} className="border-b border-gray-200 dark:border-gray-600 last:border-0">
                                                <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{item.description}</td>
                                                <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">{item.quantity}</td>
                                                <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">
                                                    {formatCurrency(item.unitPrice, invoice.currency)}
                                                </td>
                                                <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white text-right">
                                                    {formatCurrency(item.amount, invoice.currency)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Totals */}
                                <div className="border-t border-gray-200 dark:border-gray-600 p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                                        <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                                    </div>
                                    {invoice.totalTax > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Tax</span>
                                            <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.totalTax, invoice.currency)}</span>
                                        </div>
                                    )}
                                    {invoice.discount && invoice.discount.amount > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Discount</span>
                                            <span className="text-green-600 dark:text-green-400">-{formatCurrency(invoice.discount.amount, invoice.currency)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <span className="text-gray-900 dark:text-white">Total</span>
                                        <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.total, invoice.currency)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        {invoice.notes && (
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Notes</h4>
                                <p className="text-sm text-gray-700 dark:text-gray-300">{invoice.notes}</p>
                            </div>
                        )}

                        {/* Invoice Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm">
                            <div>
                                <p className="text-gray-500 dark:text-gray-400">Issue Date</p>
                                <p className="font-medium text-gray-900 dark:text-white">{formatDate(invoice.issueDate)}</p>
                            </div>
                            <div>
                                <p className="text-gray-500 dark:text-gray-400">Payment Terms</p>
                                <p className="font-medium text-gray-900 dark:text-white">{invoice.paymentTerms}</p>
                            </div>
                            {invoice.sentAt && (
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">Sent</p>
                                    <p className="font-medium text-gray-900 dark:text-white">{formatDate(invoice.sentAt)}</p>
                                </div>
                            )}
                            {invoice.remindersSent > 0 && (
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400">Reminders</p>
                                    <p className="font-medium text-gray-900 dark:text-white">{invoice.remindersSent} sent</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => window.print()}
                                className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
                            >
                                <Printer className="w-4 h-4" />
                                Print
                            </button>
                            <button
                                className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-sm"
                            >
                                <Download className="w-4 h-4" />
                                Download
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            {invoice.status === 'draft' && (
                                <button
                                    onClick={onSend}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                    <Send className="w-4 h-4" />
                                    Send Invoice
                                </button>
                            )}
                            {canRecordPayment && (
                                <>
                                    <button
                                        onClick={handleSendReminder}
                                        className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-sm font-medium"
                                    >
                                        <Clock className="w-4 h-4" />
                                        Remind
                                    </button>
                                    <button
                                        onClick={onRecordPayment}
                                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium"
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        Record Payment
                                    </button>
                                </>
                            )}
                            {isPaid && (
                                <span className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl text-sm font-medium">
                                    <CheckCircle className="w-4 h-4" />
                                    Fully Paid
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewInvoiceModal;
